/**
 * ORA - AI Leadership Agent
 * Copyright (c) 2024-2026 s2cpaul
 * Licensed under MIT License
 * Repository: https://github.com/s2cpaul/ora-agent
 */

import { Check, X, Sparkles, Users, Building2, Crown, Brain, Lock } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { useState } from 'react';
import { CheckoutForm } from './CheckoutForm';
import { AlacarteCheckoutForm } from './AlacarteCheckoutForm';
import { PersonaProCheckoutForm } from './PersonaProCheckoutForm';

interface PricingPageProps {
  onClose: () => void;
}

export function PricingPage({ onClose }: PricingPageProps) {
  const { subscription, tier, upgradeTier, loading } = useSubscription();
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<string | null>(null);
  const [checkoutPlan, setCheckoutPlan] = useState<{
    name: string;
    price: number;
    description: string;
    tierKey: string;
  } | null>(null);
  const [showAlacarteCheckout, setShowAlacarteCheckout] = useState(false);
  const [showPersonaProCheckout, setShowPersonaProCheckout] = useState(false);

  const plans = [
    {
      name: 'Free',
      price: 0,
      period: 'forever',
      icon: Sparkles,
      description: 'Perfect for trying ORA',
      features: [
        'Real-time intelligence with context!',
        'Standard AI leadership content',
        'Option to upgrade Avatar setup!',
        'AI Accelerator learning modules',
        '10 questions per month',
      ],
      limitations: [
        'No custom uploads',
        'No support service',
        'Limited content access',
      ],
      buttonText: tier === 'free' ? 'Current Plan' : 'Downgrade to Free',
      buttonStyle: tier === 'free' ? 'bg-gray-200 text-gray-700 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
      recommended: false,
      current: tier === 'free',
      tierKey: 'free' as const,
    },
    {
      name: 'AI Pro Level 3',
      subtitle: 'Solo',
      price: 15,
      period: 'month',
      icon: Crown,
      description: 'For individual AI leaders',
      features: [
        'Your voice with your personal AI Avatar',
        'Real-time intelligence with context!',
        'AI leadership video library access',
        'AI Accelerator training with chance to earn AI upgrade credits!',
        'Shareable agent link with unlimited shares',
        'Persona video creation (45 - 60 seconds) each month',
        'Unlimited questions',
        'Google Drive / SharePoint integration',
        '1 free live video consultation per month',
        'Complete data privacy',
        'Live Analytics Dashboard',
        'Option to Upgrade and add training videos!',
      ],
      limitations: [],
      buttonText: tier === 'solo' ? 'Current Plan' : 'Upgrade to Solo Pro',
      buttonStyle: tier === 'solo' ? 'bg-gray-200 text-gray-700 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white',
      recommended: true,
      current: tier === 'solo',
      tierKey: 'solo' as const,
    },
    {
      name: 'AI Pro Level 4',
      subtitle: 'Integrated AI Persona Agent',
      price: 24,
      period: 'month',
      icon: Brain,
      description: 'Create your digital persona based on established Avatar',
      subDescription: '(ID required)',
      features: [
        'Real-time intelligence with context!',
        'Everything in Solo',
        'Shareable link',
        'Connection to other Agents for parent-child relationship or 2-way data flow',
      ],
      limitations: [],
      buttonText: tier === 'avatar' ? 'Current Plan' : 'Upgrade to AI Pro Level 4',
      buttonStyle: tier === 'avatar' ? 'bg-gray-200 text-gray-700 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 text-white',
      recommended: true,
      current: tier === 'avatar',
      tierKey: 'avatar' as const,
    },
    {
      name: 'AI Pro Level 5',
      subtitle: 'Buddy',
      price: 30,
      period: 'month',
      icon: Users,
      description: '2 collaborating users',
      features: [
        'Real-time intelligence with context!',
        'Everything in Solo Pro',
        '2 user seats included for connected agents',
        'Shared knowledge base',
        'Collaboration features',
        'Connected document library',
        'Joint analytics dashboard',
        '1 free live consultation',
      ],
      limitations: [],
      buttonText: tier === 'buddy' ? 'Current Plan' : 'Upgrade to AI Pro Level 5',
      buttonStyle: tier === 'buddy' ? 'bg-gray-200 text-gray-700 cursor-not-allowed' : 'bg-black hover:bg-gray-800 text-white',
      recommended: false,
      current: tier === 'buddy',
      tierKey: 'buddy' as const,
      comingSoon: true,
    },
    {
      name: 'Pro',
      price: 33,
      period: 'month',
      icon: Brain,
      description: 'Small groups (3-4 agents)',
      features: [
        '3 Agents with connections',
        'Multi-agent collaboration',
        'Custom agent integration',
      ],
      limitations: [],
      buttonText: 'Coming Soon',
      buttonStyle: 'bg-gray-400 text-gray-700 cursor-not-allowed',
      recommended: true,
      current: false,
      tierKey: 'pro' as const,
      upgradeORA: false,
      comingSoon: true,
    },
    {
      name: 'Team',
      price: 90,
      period: 'month',
      icon: Building2,
      description: 'For larger organizations',
      features: [
        'Real-time intelligence with context!',
        'Everything in Buddy',
        '10 User seats',
        'Advanced team features',
        'Custom integrations',
        'Dedicated account manager',
        'Priority phone support',
        'Custom training sessions',
        'SLA guarantees',
        'White-label options',
      ],
      limitations: [],
      buttonText: 'Coming Soon',
      buttonStyle: 'bg-gray-300 text-gray-600 cursor-not-allowed',
      recommended: false,
      current: false,
      tierKey: 'team' as const,
      comingSoon: true,
    },
    {
      name: 'Enterprise',
      price: 299,
      period: 'month',
      icon: Building2,
      description: 'For mid to large organizations',
      features: [
        'Everything in Team',
        'Unlimited user seats',
        'Custom AI agent development',
        'White-label solutions',
        'Enterprise-grade security',
        'Dedicated success manager',
        'Priority 24/7 support',
        'Custom SLA agreements',
        'Advanced analytics & reporting',
        'API access',
      ],
      limitations: [],
      buttonText: 'Coming Soon',
      buttonStyle: 'bg-gray-300 text-gray-600 cursor-not-allowed',
      recommended: false,
      current: false,
      tierKey: 'enterprise' as const,
      comingSoon: true,
    },
  ];

  const handleUpgrade = async (planTierKey: typeof plans[number]['tierKey']) => {
    if (planTierKey === tier) return;
    
    // Prevent upgrade to coming soon plans
    const selectedPlan = plans.find(p => p.tierKey === planTierKey);
    if (selectedPlan && 'comingSoon' in selectedPlan && selectedPlan.comingSoon) {
      return;
    }
    
    if (planTierKey === 'free') {
      // Downgrade to free - just update the tier
      setIsUpgrading(true);
      try {
        await upgradeTier('free');
        alert('Successfully downgraded to Free tier');
      } catch (error: any) {
        alert(`Downgrade failed: ${error.message}`);
      } finally {
        setIsUpgrading(false);
      }
    } else {
      // For paid tiers, open checkout form
      const plan = plans.find(p => p.tierKey === planTierKey);
      if (plan) {
        setCheckoutPlan({
          name: plan.name,
          price: plan.price,
          description: plan.description,
          tierKey: plan.tierKey,
        });
      }
    }
  };

  const handleCheckoutSuccess = async (tierKey: string) => {
    // Update the tier after successful payment
    try {
      await upgradeTier(tierKey as any);
    } catch (error) {
      console.error('Failed to update tier:', error);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8">
          <p className="text-gray-700">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2 overflow-y-auto" style={{ colorScheme: 'light' }}>
      {/* Checkout Form Modal */}
      {checkoutPlan && (
        <CheckoutForm
          planName={checkoutPlan.name}
          planPrice={checkoutPlan.price}
          planDescription={checkoutPlan.description}
          onClose={() => setCheckoutPlan(null)}
          onSuccess={handleCheckoutSuccess}
        />
      )}
      
      {/* AI Pro Level 2 Checkout Form Modal */}
      {showAlacarteCheckout && (
        <AlacarteCheckoutForm
          onClose={() => setShowAlacarteCheckout(false)}
        />
      )}
      
      {/* Persona Pro Checkout Form Modal */}
      {showPersonaProCheckout && (
        <PersonaProCheckoutForm
          onClose={() => setShowPersonaProCheckout(false)}
        />
      )}
      
      <div className="bg-white rounded-lg max-w-sm w-full shadow-2xl border border-gray-300 my-2 max-h-[90vh] overflow-y-auto" style={{ colorScheme: 'light' }}>
        {/* Header - FORCED WHITE TEXT */}
        <div style={{ background: 'linear-gradient(to right, #2563EB, #14B8A6)', padding: '12px', borderRadius: '8px 8px 0 0', position: 'relative', zIndex: 10, colorScheme: 'light', color: '#ffffff !important' }}>
          <button
            onClick={onClose}
            style={{ position: 'absolute', top: '8px', right: '8px', padding: '4px', borderRadius: '6px', color: '#ffffff', background: 'transparent', border: 'none', cursor: 'pointer', zIndex: 20 }}
            aria-label="Close pricing modal"
          >
            <X style={{ width: '16px', height: '16px', color: '#ffffff !important' }} />
          </button>
          <div style={{ textAlign: 'center' }}>
            <h1 style={{ color: '#ffffff !important', fontWeight: 'bold', fontSize: '16px', margin: '0 0 4px 0' }}>Upgrade ORA</h1>
            <p style={{ color: '#ffffff !important', fontSize: '12px', margin: '0' }}>
              Choose your plan
            </p>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="p-3 space-y-3 shadow-lg shadow-purple-900/30">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isComingSoon = 'comingSoon' in plan && plan.comingSoon;
            return (
              <div key={plan.name}>
                <div
                  className={`relative rounded-lg border-2 p-3 ${
                    plan.recommended && (plan.name === 'AI Pro Level 3' || plan.name === 'Solo Pro')
                      ? 'border-blue-600 shadow-lg bg-blue-50'
                      : plan.recommended && plan.name === 'AI Pro Level 4'
                      ? 'border-purple-600 shadow-lg bg-purple-50'
                      : plan.recommended && plan.name === 'Pro'
                      ? 'border-teal-600 shadow-lg bg-teal-50'
                      : plan.name === 'AI Pro Level 5'
                      ? 'border-yellow-500 shadow-lg bg-yellow-50'
                      : isComingSoon
                      ? 'border-gray-300 bg-gray-50 opacity-75'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {plan.recommended && (plan.name === 'AI Pro Level 3' || plan.name === 'Solo Pro') && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-2 py-0.5 rounded-full text-[9px] font-bold">
                      POPULAR UPGRADE
                    </div>
                  )}

                  {plan.recommended && plan.name === 'AI Pro Level 4' && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-purple-600 text-white px-2 py-0.5 rounded-full text-[9px] font-bold">
                      POPULAR
                    </div>
                  )}

                  {plan.recommended && plan.name === 'Pro' && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-teal-600 text-white px-2 py-0.5 rounded-full text-[9px] font-bold">
                      MOST POPULAR
                    </div>
                  )}

                  {isComingSoon && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gray-600 text-white px-2 py-0.5 rounded-full text-[9px] font-bold flex items-center gap-1">
                      <Lock className="size-2" />
                      COMING SOON
                    </div>
                  )}

                  {/* Plan Header - Single Line */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon className="size-3 text-black" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-blue-900">{plan.name}</h3>
                        {'subtitle' in plan && plan.subtitle && (
                          <p className="text-[10px] font-semibold text-black">{plan.subtitle}</p>
                        )}
                        <p className="text-[9px] text-black">{plan.description}</p>
                        {'subDescription' in plan && plan.subDescription && (
                          <p className="text-[9px] text-black">{plan.subDescription}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg font-bold text-black">${plan.price}</div>
                      <div className="text-[9px] text-black">/{plan.period}</div>
                    </div>
                  </div>

                  {/* Features - Compact */}
                  <ul className="space-y-1 mb-2">
                    {/* Special display for Pro plan - show "3 Agents" */}
                    {plan.name === 'Pro' && (
                      <li className="flex items-center gap-2 mb-1 pb-1 border-b border-gray-200">
                        <Brain className="size-4 text-teal-600 fill-yellow-400" />
                        <span className="text-sm font-bold text-black">3 Agents</span>
                      </li>
                    )}
                    {(expandedPlan === plan.name ? plan.features : plan.features.slice(0, 3)).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-1.5 text-[10px]">
                        <Check className="size-2.5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-black leading-tight">{feature}</span>
                      </li>
                    ))}
                    {plan.features.length > 3 && (
                      <li>
                        <button
                          onClick={() => setExpandedPlan(expandedPlan === plan.name ? null : plan.name)}
                          className="text-[9px] text-black hover:text-gray-700 font-medium italic ml-3.5 underline"
                        >
                          {expandedPlan === plan.name ? 'Show less' : `+${plan.features.length - 3} more features`}
                        </button>
                      </li>
                    )}
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleUpgrade(plan.tierKey)}
                    disabled={plan.current || isUpgrading}
                    className={`w-full py-1.5 px-3 rounded-lg font-semibold transition-all text-[10px] ${plan.buttonStyle}`}
                  >
                    {plan.buttonText}
                  </button>
                </div>

                {/* AI Pro Level 2 option after Free plan */}
                {plan.name === 'Free' && (
                  <>
                    {/* Purple Card - Avatar Basic Template */}
                    <div className="bg-gradient-to-br from-purple-100 to-indigo-100 border-2 border-purple-400 rounded-lg p-3">
                      <div className="text-center mb-2">
                        <h3 className="font-bold text-xs !text-[#000000] mb-1">AI Pro Level 1</h3>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                          <Brain className="size-3 text-white fill-yellow-400" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-bold text-xs !text-[#000000]">Avatar Basic Template</h4>
                            <div className="text-sm font-bold !text-[#000000]">$24<span className="text-[9px] !text-[#000000]"></span></div>
                          </div>
                          
                          <ul className="text-[9px] !text-[#000000] space-y-1 mb-2">
                            <li>• You script (20-30 words)</li>
                            <li>• No revisions</li>
                            <li>• The Avatar Base Template becomes the foundation for your persona and much more!</li>
                          </ul>
                          
                          <button
                            onClick={() => setShowAlacarteCheckout(true)}
                            className="w-full px-2 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-[9px] font-semibold transition-colors"
                          >
                            Start Now
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Green Card - Existing Avatar Upgrade */}
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-lg p-3 relative">
                      {/* Popular Badge */}
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-600 text-white px-2 py-0.5 rounded-full text-[9px] font-bold">
                        POPULAR
                      </div>
                      
                      <div className="text-center mb-2">
                        <h3 className="font-bold text-xs !text-[#000000] mb-1">AI Pro Level 2</h3>
                        <p className="text-[9px] !text-green-600 font-semibold">No monthly commitment</p>
                      </div>
                      
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                          <Brain className="size-3 text-white fill-yellow-400" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-bold text-xs !text-[#000000]">Persona Pro Video Intro</h4>
                            <div className="text-sm font-bold !text-[#000000]">$24<span className="text-[9px] !text-[#000000]"></span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-3 bg-gray-50 rounded-b-lg">
          <p className="text-[9px] text-black text-center">
            All plans include 30-day money-back guarantee
          </p>
        </div>
      </div>
    </div>
  );
}